// README:
// This module converts a stable string (e.g., FingerprintJS visitorId) into a deterministic UUID.
// - Default strategy: SHA-256 -> RFC4122 (v4 style bits) using Web Crypto (browser/Node18+).
// - Alternate strategy: UUIDv5 (name-based) with a fixed namespace.
// Usage:
//   import { fingerprintToUuid } from './fingerprintToUuid';
//   const id = await fingerprintToUuid('da1d90ec4d10cee604f4e4ac82fc40e6');
//   const idSalted = await fingerprintToUuid('da1d90...', { salt: 'my-secret' });
//   const idV5 = await fingerprintToUuid('da1d90...', { strategy: 'v5' });

import { v5 as uuidv5 } from 'uuid';

// webpack replaces this with the runtime require; declared so tsc is happy.
declare const __non_webpack_require__: ((m: string) => any) | undefined;

type Options = {
  salt?: string;
  strategy?: 'sha256' | 'v5';
};

// Pick a constant private namespace UUID (generate one once for your app)
// or read from env if provided.
const DEFAULT_NAMESPACE =
  process.env.UUID_V5_NAMESPACE?.trim() ||
  '7e01b7b5-9baf-4a5d-9e9e-63e0f3a9a111';

function getSubtle(): SubtleCrypto {
  // Browser
  if (typeof globalThis !== 'undefined' && (globalThis as any).crypto?.subtle) {
    return (globalThis as any).crypto.subtle;
  }
  
  // Node 18+ (real Node runtime only; never the content script, which always
  // returns above via globalThis.crypto.subtle). We use webpack's
  // `__non_webpack_require__` escape hatch so this stays a genuine RUNTIME
  // require in Node but is NOT statically bundled — otherwise webpack's
  // `crypto` → `crypto-browserify` fallback drags ~485KB (bn.js + elliptic +
  // 107 modules) into app.js purely for this dead-in-browser branch.
  try {
    // __non_webpack_require__ is replaced by webpack with the runtime require;
    // in jest/ts-node it is undefined, so fall back to the normal require.
    const nodeRequire: ((m: string) => any) | undefined =
      typeof __non_webpack_require__ !== 'undefined'
        ? __non_webpack_require__
        : typeof require !== 'undefined'
          ? require
          : undefined;
    if (nodeRequire) {
      const { webcrypto } = nodeRequire('crypto');
      if (webcrypto?.subtle) return webcrypto.subtle;
    }
  } catch {
    // Ignore require errors in browser environments
  }

  // Fallback for older Node environments or when crypto is not available
  throw new Error(
    'Web Crypto API not available. In Node, use Node >= 18 or enable global WebCrypto.'
  );
}

function bytesToUuid(b: Uint8Array): string {
  const hex = [...b].map((x) => x.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(
    16,
    20
  )}-${hex.slice(20)}`;
}

async function sha256ToUuid(input: string, salt = ''): Promise<string> {
  const subtle = getSubtle();
  const enc = new TextEncoder();
  const data = enc.encode(salt + input);
  const hashBuf = await subtle.digest('SHA-256', data);
  const hash = new Uint8Array(hashBuf);

  // Take first 16 bytes and set RFC4122 bits (version 4 style, but deterministic)
  const b = hash.slice(0, 16);
  b[6] = (b[6] & 0x0f) | 0x40; // version 4
  b[8] = (b[8] & 0x3f) | 0x80; // variant 10xx

  return bytesToUuid(b);
}

export async function fingerprintToUuid(
  input: string,
  opts: Options = {}
): Promise<string> {
  const strategy = opts.strategy ?? 'sha256';
  const salt = opts.salt ?? '';
  const normalized = (input ?? '').trim();
  if (!normalized) throw new Error('input required');

  if (strategy === 'v5') {
    // UUIDv5 is deterministic: same namespace + same name -> same UUID
    return uuidv5(salt + normalized, DEFAULT_NAMESPACE);
  }

  // default: sha256
  return sha256ToUuid(normalized, salt);
}
